import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Header from "./Header";
import LoadingSpinner from "./LoadingSpinner";
import Carousel from "./Carousel";
import { ChevronsRight, MessageCircle } from 'lucide-react';
import { openTelegramLink } from "./../telegram-utils.js";
import { fetchItem, fetchMuseumItem } from "../api";

// mode: 'item' | 'museum'
export default function ItemDetailsPage({ mode }) {
  const { itemId } = useParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      const fetcher = mode === 'museum' ? fetchMuseumItem : fetchItem;
      const data = await fetcher(itemId);
      if (!cancelled) {
        setItem(data);
        setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [mode, itemId]);

  if (loading || !item) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-800">
        {/* No explicit onBack: falls back to browser-history back in Header,
            since we always arrive here from whichever list we just came from. */}
        <Header title="Item Details" />
        <div className="max-w-4xl mx-auto p-4 sm:p-6 lg:p-8">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  const messageText = encodeURIComponent(`Hello, I would like to contact you regarding ${item?.title}`);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-800">
      <Header title="Item Details" />

      <div className="max-w-4xl mx-auto p-4 sm:p-6 lg:p-8">
        <div className="bg-white dark:bg-gray-700 rounded-lg shadow-sm border border-gray-200 dark:border-gray-500 overflow-hidden">
          {item.photoUrls.length > 0 && (
            <Carousel item={item} />
          )}
          <div className="p-4 sm:p-6 lg:p-8">
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-800 dark:text-white mb-2">
              {item?.title}
            </h2>
            {item.specialOffer && !item.archived && (
              <div className="text-xl sm:text-2xl font-bold mb-4">
                <span className="text-pink-700 dark:text-pink-600">
                  {item.specialOfferPrice.toFixed(0)} RSD
                </span>
                <span className="line-through ml-3 text-indigo-600 dark:text-indigo-200">
                  {item.price.toFixed(0)} RSD
                </span>
              </div>
            )}
            {item.price && !item.specialOffer && !item.archived && (
              <div className="text-xl sm:text-2xl font-bold text-indigo-600 dark:text-indigo-200 mb-4">
                {item?.price.toFixed(0)} RSD
              </div>
            )}
            {item.reserved && !item.archived && (
              <div className="text-xl sm:text-2xl font-bold text-indigo-600 dark:text-indigo-200 mb-4">
                RESERVED
              </div>
            )}
            {item.archived && (
              <div className="text-xl sm:text-2xl font-bold text-indigo-600 dark:text-indigo-200 mb-4">
                SOLD
              </div>
            )}
            <p className="text-gray-600 dark:text-white mb-6 leading-relaxed text-sm sm:text-base">
              {item?.description}
            </p>
            <div className="space-y-3 sm:space-y-0 sm:flex sm:gap-3">
              {item.post && (
                <button
                  onClick={() => openTelegramLink(item?.post)}
                  className="w-full sm:flex-1 bg-indigo-900 hover:bg-indigo-800 text-white font-semibold py-3 px-4 sm:px-6 rounded-lg transition-colors flex items-center justify-center space-x-2"
                >
                  <ChevronsRight className="h-5 w-5" />
                  <span className="text-sm sm:text-base">Read my review</span>
                </button>
              )}
              <button
                onClick={() => openTelegramLink(`https://telegram.me/Neformall?text=${messageText}`)}
                className="w-full sm:flex-1 bg-indigo-900 hover:bg-indigo-800 text-white font-semibold py-3 px-4 sm:px-6 rounded-lg transition-colors flex items-center justify-center space-x-2"
              >
                <MessageCircle className="h-5 w-5" />
                <span className="text-sm sm:text-base">Contact me</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}